import { ArrowLeftIcon, ChevronDownIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import {
  ActionIcon,
  Alert,
  Badge,
  Box,
  Button,
  Divider,
  Grid,
  Group,
  List,
  Loader,
  NumberInput,
  Paper,
  Select,
  Stack,
  Text,
  Textarea,
  TextInput,
  Title,
  useMantineColorScheme,
} from '@mantine/core';
import { DateInput } from '@mantine/dates';
import '@mantine/dates/styles.css';
import { notifications } from '@mantine/notifications';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ROUTES } from '../../../constants/routes';
import { useCertificatesDictionary } from '../../../hooks/queries/useCertificatesDictionary';
import { useCertificationFamiliesDictionary } from '../../../hooks/queries/useCertificationFamiliesDictionary';
import { useLanguageDictionary, useLanguageProficiencies } from '../../../hooks/queries/useLanguageDictionary';
import { useTechnologyCanonicalsDictionary } from '../../../hooks/queries/useTechnologyCanonicalsDictionary';
import {
  CreateOpportunityRequest,
  OpportunityStatus,
  OpportunityType,
  PositionRequest,
  UpdateOpportunityRequest,
} from '../../../models/opportunity.model';
import { OfficeLocationService } from '../../../services/officeLocationService';
import { PositionEditor, PositionFormRow } from '../components/PositionEditor';
import {
  useCreateOpportunity,
  useUpdateOpportunity,
} from '../hooks/useOpportunityMutations';
import { useOpportunityQuery } from '../hooks/useOpportunityQuery';
import { useAddPosition, useRemovePosition, useUpdatePosition } from '../hooks/usePositionMutations';

const officeLocationSvc = new OfficeLocationService();

const TYPE_OPTIONS = [
  { value: String(OpportunityType.FullTime), label: 'Full-Time' },
  { value: String(OpportunityType.PartTime), label: 'Part-Time' },
  { value: String(OpportunityType.StretchProject), label: 'Stretch Project' },
  { value: String(OpportunityType.Mentorship), label: 'Mentorship' },
  { value: String(OpportunityType.JobShadow), label: 'Job Shadow' },
];

const STATUS_OPTIONS = [
  { value: String(OpportunityStatus.Draft), label: 'Draft' },
  { value: String(OpportunityStatus.Proposed), label: 'Proposed' },
  { value: String(OpportunityStatus.Bid), label: 'Bid' },
  { value: String(OpportunityStatus.Live), label: 'Live' },

];

interface HeaderFormState {
  title: string;
  description: string;
  opportunityType: OpportunityType;
  opportunityStatus: OpportunityStatus;
  requiredAvailabilityDate: Date | null;
  timeCommitmentPercent: number | null;
  targetOfficeLocationId: number | null;
  availabilityWeightPercent: number | null;
}

const defaultHeader = (): HeaderFormState => ({
  title: '',
  description: '',
  opportunityType: OpportunityType.None,
  opportunityStatus: OpportunityStatus.Draft,
  requiredAvailabilityDate: null,
  timeCommitmentPercent: null,
  targetOfficeLocationId: null,
  availabilityWeightPercent: null,
});

const defaultPosition = (sortOrder: number): PositionFormRow => ({
  title: '',
  description: '',
  headCount: 1,
  sortOrder,
  technologies: [],
  certifications: [],
  languages: [],
});

const toDateString = (d: Date | null): string | null =>
  d ? d.toISOString().split('T')[0] : null;

const positionToRequest = (p: PositionFormRow, index: number): PositionRequest => ({
  title: p.title.trim(),
  description: p.description.trim() || null,
  headCount: p.headCount,
  sortOrder: index,
  technologies: p.technologies
    .filter((t) => t.technologyCanonicalId > 0)
    .map((t) => ({ technologyCanonicalId: t.technologyCanonicalId, requiredLevel: t.requiredLevel, requiredWeight: t.requiredWeight })),
  certifications: p.certifications
    .filter((c) => c.certificateId != null || c.certificationFamilyId != null)
    .map((c) => ({ certificateId: c.certificateId, certificationFamilyId: c.certificationFamilyId })),
  languages: p.languages
    .filter((l) => l.languageId > 0)
    .map((l) => ({ languageId: l.languageId, requiredProficiencyId: l.requiredProficiencyId, requiredWeight: l.requiredWeight })),
});

export const OpportunityFormPage = () => {
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const isEdit = !!id;
  const { colorScheme } = useMantineColorScheme();
  const sectionLabelColor = colorScheme === 'dark' ? 'brand.3' : 'brand.5';
  const opportunityId = id ? parseInt(id, 10) : 0;

  const { data: existing, isLoading: loadingExisting } = useOpportunityQuery(opportunityId);

  // Pre-warm dictionary caches
  useTechnologyCanonicalsDictionary();
  useCertificatesDictionary();
  useCertificationFamiliesDictionary();
  useLanguageDictionary();
  useLanguageProficiencies();

  const { data: officeLocations } = useQuery({
    queryKey: ['officeLocations', 'dictionary'],
    queryFn: () => officeLocationSvc.getOfficeLocationsDictionary(),
    staleTime: 15 * 60 * 1000,
  });

  const officeOptions = useMemo(
    () => officeLocations?.map((o) => ({ value: o.id.toString(), label: o.name })) ?? [],
    [officeLocations]
  );

  const [header, setHeader] = useState<HeaderFormState>(defaultHeader());
  const [positions, setPositions] = useState<PositionFormRow[]>([defaultPosition(0)]);
  const [errors, setErrors] = useState<string[]>([]);
  const [submitted, setSubmitted] = useState(false);
  const [expandedPositions, setExpandedPositions] = useState<Set<number>>(new Set([0]));

  useEffect(() => {
    if (existing && isEdit) {
      setHeader({
        title: existing.title,
        description: existing.description ?? '',
        opportunityType: existing.opportunityType,
        opportunityStatus: existing.opportunityStatus,
        requiredAvailabilityDate: existing.requiredAvailabilityDate
          ? new Date(existing.requiredAvailabilityDate)
          : null,
        timeCommitmentPercent: existing.timeCommitmentPercent ?? null,
        targetOfficeLocationId: existing.targetOfficeLocationId ?? null,
        availabilityWeightPercent: existing.availabilityWeightPercent ?? null,
      });
      if (existing.positions.length > 0) {
        setPositions(
          existing.positions.map((p) => ({
            title: p.title,
            description: p.description ?? '',
            headCount: p.headCount,
            sortOrder: p.sortOrder,
            technologies: p.technologies.map((t) => ({
              technologyCanonicalId: t.canonicalId,
              canonicalName: t.canonicalName,
              requiredLevel: t.requiredLevel,
              requiredWeight: t.requiredWeight,
            })),
            certifications: p.certifications.map((c) => ({
              mode: c.certificationFamilyId != null ? 'family' as const : 'cert' as const,
              certificateId: c.certificateId,
              certificateTitle: c.certificateTitle,
              certificationFamilyId: c.certificationFamilyId,
              certificationFamilyName: c.certificationFamilyName,
            })),
            languages: p.languages.map((l) => ({
              languageId: l.languageId,
              languageNativeName: l.languageNativeName,
              requiredProficiencyId: l.requiredProficiencyId,
              requiredProficiencyName: l.requiredProficiencyName,
              requiredWeight: l.requiredWeight,
            })),
          }))
        );
      }
    }
  }, [existing, isEdit]);

  const createMutation = useCreateOpportunity();
  const updateMutation = useUpdateOpportunity(opportunityId);
  const addPositionMutation = useAddPosition(opportunityId);
  const updatePositionMutation = useUpdatePosition(opportunityId);
  const removePositionMutation = useRemovePosition(opportunityId);

  const isSaving =
    createMutation.isPending ||
    updateMutation.isPending ||
    addPositionMutation.isPending ||
    updatePositionMutation.isPending ||
    removePositionMutation.isPending;

  const setHeaderField = <K extends keyof HeaderFormState>(key: K, value: HeaderFormState[K]) =>
    setHeader((prev) => ({ ...prev, [key]: value }));

  const validate = (): string[] => {
    const errs: string[] = [];
    if (!header.title.trim()) {
      errs.push('Title is required.');
    }
    if (header.opportunityType === OpportunityType.None) {
      errs.push('Opportunity type must be selected.');
    }
    if (positions.length === 0) {
      errs.push('At least one position is required.');
    }
    positions.forEach((p, i) => {
      if (!p.title.trim()) {
        errs.push(`Position ${i + 1}: title is required.`);
      }
    });
    return errs;
  };

  const handleSave = async () => {
    setSubmitted(true);
    const errs = validate();
    setErrors(errs);
    if (errs.length > 0) {
      return;
    }

    try {
      if (isEdit) {
        const updateRequest: UpdateOpportunityRequest = {
          title: header.title.trim(),
          description: header.description.trim() || null,
          opportunityType: header.opportunityType,
          opportunityStatus: header.opportunityStatus,
          requiredAvailabilityDate: toDateString(header.requiredAvailabilityDate),
          timeCommitmentPercent: header.timeCommitmentPercent,
          targetOfficeLocationId: header.targetOfficeLocationId,
          availabilityWeightPercent: header.availabilityWeightPercent,
        };
        await updateMutation.mutateAsync(updateRequest);

        // Sync positions: remove deleted, update existing, add new
        const existingPositions = existing?.positions ?? [];
        const currentIds = new Set(
          existingPositions.map((p) => p.id)
        );

        for (const existingPos of existingPositions) {
          const matchIndex = existingPositions.indexOf(existingPos);
          if (matchIndex < positions.length) {
            await updatePositionMutation.mutateAsync({
              positionId: existingPos.id,
              data: positionToRequest(positions[matchIndex], matchIndex),
            });
          } else {
            await removePositionMutation.mutateAsync(existingPos.id);
          }
        }

        // Add any new positions beyond the existing count
        for (let i = currentIds.size; i < positions.length; i++) {
          await addPositionMutation.mutateAsync(positionToRequest(positions[i], i));
        }

        notifications.show({ title: 'Saved', message: 'Opportunity updated.', color: 'green' });
        navigate(ROUTES.OPPORTUNITY_DETAIL(opportunityId));
      } else {
        const createRequest: CreateOpportunityRequest = {
          title: header.title.trim(),
          description: header.description.trim() || null,
          opportunityType: header.opportunityType,
          opportunityStatus: header.opportunityStatus,
          requiredAvailabilityDate: toDateString(header.requiredAvailabilityDate),
          timeCommitmentPercent: header.timeCommitmentPercent,
          targetOfficeLocationId: header.targetOfficeLocationId,
          availabilityWeightPercent: header.availabilityWeightPercent,
          positions: positions.map((p, idx) => positionToRequest(p, idx)),
        };
        const created = await createMutation.mutateAsync(createRequest);
        notifications.show({ title: 'Created', message: 'Opportunity created.', color: 'green' });
        navigate(ROUTES.OPPORTUNITY_DETAIL(created.id));
      }
    } catch {
      notifications.show({ title: 'Error', message: 'Failed to save. Please try again.', color: 'red' });
    }
  };

  const handleAddPosition = useCallback(() => {
    setPositions((prev) => {
      const newIdx = prev.length;
      setExpandedPositions((s) => new Set([...s, newIdx]));
      return [...prev, defaultPosition(prev.length)];
    });
  }, []);

  const handleRemovePosition = useCallback((index: number) => {
    setPositions((prev) => prev.filter((_, i) => i !== index));
    setExpandedPositions((prev) => {
      const next = new Set<number>();
      for (const n of prev) {
        if (n < index) { next.add(n); }
        else if (n > index) { next.add(n - 1); }
      }
      return next;
    });
  }, []);

  const handlePositionChange = useCallback((index: number, value: PositionFormRow) => {
    setPositions((prev) => prev.map((p, i) => (i === index ? value : p)));
  }, []);

  const togglePosition = useCallback((idx: number) => {
    setExpandedPositions((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) { next.delete(idx); } else { next.add(idx); }
      return next;
    });
  }, []);

  if (isEdit && loadingExisting) {
    return (
      <Stack align="center" pt="xl">
        <Loader />
      </Stack>
    );
  }

  return (
    <Stack gap="md" maw={1200} mx="auto" px="md" pb="xl">

      {/* ── Sticky top bar ────────────────────────────────────────────────── */}
      <Box
        style={{
          position: 'sticky',
          top: 60,
          zIndex: 100,
          backgroundColor: 'var(--mantine-color-body)',
          borderBottom: '1px solid var(--mantine-color-default-border)',
          padding: '12px 0',
          marginBottom: 8,
        }}
      >
        <Group justify="space-between" align="center">
          <Group gap="xs">
            <ActionIcon variant="subtle" color="gray" onClick={() => navigate(-1)} aria-label="Back">
              <ArrowLeftIcon style={{ width: 18, height: 18 }} />
            </ActionIcon>
            <Title order={3}>{isEdit ? 'Edit Opportunity' : 'New Opportunity'}</Title>
            {header.title.trim() && (
              <Text c="dimmed" size="lg" style={{ fontWeight: 400 }}>— {header.title}</Text>
            )}
          </Group>
          <Group gap="sm">
            <Button variant="default" onClick={() => navigate(-1)} disabled={isSaving}>
              Cancel
            </Button>
            <Button onClick={handleSave} loading={isSaving}>
              {isEdit ? 'Save Changes' : 'Create Opportunity'}
            </Button>
          </Group>
        </Group>
      </Box>

      {submitted && errors.length > 0 && (
        <Alert color="red" title="Please fix the following errors">
          <List size="sm" spacing={2}>
            {errors.map((e) => (
              <List.Item key={e}>{e}</List.Item>
            ))}
          </List>
        </Alert>
      )}

      <Grid>
        {/* Core header fields */}
        <Grid.Col span={{ base: 12, md: 8 }}>
          <Paper withBorder p="md" radius="md" style={{ borderLeft: '3px solid var(--mantine-color-brand-2)' }}>
            <Stack gap="md">
              <Text fw={600} size="sm" c={sectionLabelColor}>OPPORTUNITY DETAILS</Text>
              <TextInput
                label="Title"
                placeholder="e.g. Warsaw Java Team Expansion"
                required
                value={header.title}
                onChange={(e) => setHeaderField('title', e.currentTarget.value)}
                error={submitted && !header.title.trim() ? 'Required' : undefined}
              />

              <Grid>
                <Grid.Col span={6}>
                  <Select
                    label="Type"
                    required
                    data={TYPE_OPTIONS}
                    value={header.opportunityType !== OpportunityType.None ? String(header.opportunityType) : null}
                    onChange={(v) =>
                      setHeaderField('opportunityType', v ? (parseInt(v) as OpportunityType) : OpportunityType.None)
                    }
                    error={submitted && header.opportunityType === OpportunityType.None ? 'Required' : undefined}
                  />
                </Grid.Col>
                <Grid.Col span={6}>
                  <Select
                    label="Status"
                    data={STATUS_OPTIONS}
                    value={String(header.opportunityStatus)}
                    onChange={(v) =>
                      setHeaderField('opportunityStatus', v ? (parseInt(v) as OpportunityStatus) : OpportunityStatus.Draft)
                    }
                  />
                </Grid.Col>
              </Grid>

              <Textarea
                label="Description"
                placeholder="Describe the opportunity, context, expected outcomes…"
                minRows={3}
                autosize
                value={header.description}
                onChange={(e) => setHeaderField('description', e.currentTarget.value)}
              />
            </Stack>
          </Paper>
        </Grid.Col>

        {/* Side fields */}
        <Grid.Col span={{ base: 12, md: 4 }}>
          <Paper withBorder p="md" radius="md" h="100%" style={{ borderLeft: '3px solid var(--mantine-color-brand-2)' }}>
            <Stack gap="md">
              <Text fw={600} size="sm" c={sectionLabelColor}>SCHEDULING & STAFFING</Text>
              <DateInput
                label="Required Availability"
                placeholder="Pick a date"
                value={header.requiredAvailabilityDate}
                onChange={(v) => setHeaderField('requiredAvailabilityDate', v)}
                clearable
              />

              <NumberInput
                label="Time Commitment %"
                placeholder="e.g. 50"
                min={1}
                max={100}
                value={header.timeCommitmentPercent ?? ''}
                onChange={(v) => setHeaderField('timeCommitmentPercent', typeof v === 'number' ? v : null)}
              />

              <Select
                label="Target Office"
                placeholder="Any"
                searchable
                clearable
                data={officeOptions}
                value={header.targetOfficeLocationId?.toString() ?? null}
                onChange={(v) => setHeaderField('targetOfficeLocationId', v ? parseInt(v) : null)}
              />

              <NumberInput
                label="Availability Weight %"
                description="How much availability date matters (0–30)"
                placeholder="e.g. 10"
                min={0}
                max={30}
                value={header.availabilityWeightPercent ?? ''}
                onChange={(v) => setHeaderField('availabilityWeightPercent', typeof v === 'number' ? v : null)}
              />
            </Stack>
          </Paper>
        </Grid.Col>

        {/* Positions section */}
        <Grid.Col span={12}>
          <Group justify="space-between" align="center" mt="xs" mb={4}>
            <Group gap="xs">
              <Text fw={600} size="sm">Positions</Text>
              <Text size="xs" c="dimmed">({positions.length})</Text>
            </Group>
            <Button
              variant="light"
              size="xs"
              leftSection={<PlusIcon style={{ width: 13, height: 13 }} />}
              onClick={handleAddPosition}
            >
              Add Position
            </Button>
          </Group>
          <Divider />
        </Grid.Col>

        <Grid.Col span={12}>
          <Stack gap="xs">
            {positions.map((pos, idx) => {
              const isExpanded = expandedPositions.has(idx);
              const displayTitle = pos.title.trim() || 'New Position';
              const skillSummary = [
                pos.technologies.length > 0 && `${pos.technologies.length} tech`,
                pos.certifications.length > 0 && `${pos.certifications.length} cert`,
                pos.languages.length > 0 && `${pos.languages.length} lang`,
              ].filter(Boolean).join(' · ');

              if (isExpanded) {
                return (
                  <PositionEditor
                    key={idx}
                    index={idx}
                    position={pos}
                    onChange={(v) => handlePositionChange(idx, v)}
                    onRemove={() => handleRemovePosition(idx)}
                    canRemove={positions.length > 1}
                    showErrors={submitted}
                    onCollapse={() => togglePosition(idx)}
                  />
                );
              }

              return (
                <Paper
                  key={idx}
                  withBorder
                  p="sm"
                  radius="md"
                  onClick={() => togglePosition(idx)}
                  style={{
                    borderLeft: '3px solid var(--mantine-color-brand-5)',
                    cursor: 'pointer',
                  }}
                  onMouseEnter={(e) => {
                    (e.currentTarget as HTMLElement).style.boxShadow = '0 2px 8px rgba(0,0,0,0.08)';
                  }}
                  onMouseLeave={(e) => {
                    (e.currentTarget as HTMLElement).style.boxShadow = '';
                  }}
                >
                  <Group justify="space-between" align="center">
                    <Group gap="sm" align="center" style={{ flex: 1, minWidth: 0 }}>
                      <Badge variant="filled" color="brand.5" size="sm" circle style={{ flexShrink: 0 }}>
                        {idx + 1}
                      </Badge>
                      <Text size="sm" fw={600} lineClamp={1}>{displayTitle}</Text>
                      {pos.headCount > 1 && (
                        <Text size="xs" c="dimmed" style={{ flexShrink: 0 }}>× {pos.headCount}</Text>
                      )}
                      {skillSummary && (
                        <Text size="xs" c="dimmed" style={{ flexShrink: 0 }}>{skillSummary}</Text>
                      )}
                      {submitted && !pos.title.trim() && (
                        <Badge size="xs" color="red" variant="light" style={{ flexShrink: 0 }}>Title required</Badge>
                      )}
                    </Group>
                    <Group gap={6} align="center" style={{ flexShrink: 0 }}>
                      {positions.length > 1 && (
                        <Button
                          variant="subtle"
                          color="red"
                          size="xs"
                          leftSection={<TrashIcon style={{ width: 13, height: 13 }} />}
                          onClick={(e) => { e.stopPropagation(); handleRemovePosition(idx); }}
                        >
                          Remove
                        </Button>
                      )}
                      <ChevronDownIcon style={{ width: 14, height: 14, color: 'var(--mantine-color-dimmed)' }} />
                    </Group>
                  </Group>
                </Paper>
              );
            })}
          </Stack>
        </Grid.Col>
      </Grid>
    </Stack>
  );
};
